import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Check,
  X,
  RefreshCcw,
  Save,
  Eye,
  EyeOff,
  Zap,
  Trophy,
} from "lucide-react";
import { api } from "@/lib/api";

const GROUPS = [
  { id: "crypto", label: "Crypto data", provider: "CoinGecko / CoinCap" },
  { id: "stocks", label: "Stocks", provider: "Alpha Vantage" },
  { id: "bonds", label: "Bonds", provider: "Alpha Vantage" },
  { id: "payments", label: "Payments", provider: "Stripe / PayPal" },
  { id: "email", label: "Email", provider: "Resend (Emergent-managed)" },
];

export default function AdminProviders() {
  const [providers, setProviders] = useState([]);
  const [status, setStatus] = useState({});
  const [drafts, setDrafts] = useState({});
  const [reveal, setReveal] = useState({});
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState({});

  const load = useCallback(async () => {
    try {
      const [{ data: settings }, { data: st }] = await Promise.all([
        api.get("/admin/providers"),
        api.get("/admin/providers/status"),
      ]);
      setProviders(settings.providers);
      setStatus(st);
      setDrafts({});
    } catch (e) {
      toast.error("Failed to load providers");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(() => Object.keys(drafts).length > 0, [drafts]);

  const setDraft = (key, value) => setDrafts((prev) => ({ ...prev, [key]: value }));
  const clearDraft = (key) =>
    setDrafts((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/providers", { updates: drafts });
      toast.success("Settings saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const reset = async (key) => {
    setBusy(true);
    try {
      await api.put("/admin/providers", { updates: { [key]: "" } });
      toast.success("Reset to default");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const setMode = async (group, mode) => {
    try {
      await api.put(`/admin/providers/mode/${group}`, { mode });
      toast.success(
        mode === "auto"
          ? `${group}: auto-optimize enabled`
          : `${group}: pinned to ${mode}`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set mode");
    }
  };

  const runTest = async (group) => {
    setTesting((t) => ({ ...t, [group]: true }));
    try {
      if (group === "email") {
        const { data } = await api.post("/admin/email/test");
        if (data.ok) {
          toast.success(`Test email sent to ${data.to}`, {
            description: data.note || undefined,
          });
        } else {
          toast.error(`Email test failed: ${data.error || "unknown"}`);
        }
      } else {
        const { data } = await api.post(`/admin/providers/test/${group}`);
        toast[data.ok ? "success" : "error"](`${group}: ${data.detail}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting((t) => ({ ...t, [group]: false }));
    }
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="admin-providers-page">
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            // admin · providers
          </div>
          <h1 className="mt-2 text-4xl md:text-5xl font-black tracking-tighter uppercase">
            Providers
          </h1>
          <p className="mt-3 text-sm text-neutral-400 max-w-2xl">
            Swap or auto-optimize the data & payment providers behind Neo.Invest.
            In <span className="text-white">Auto (profit-optimize)</span> mode each group
            is scored by <span className="font-mono">success_rate × exp(−latency/1500)</span>
            and the highest-scoring provider is used on every fetch. Fees keep flowing to
            the ledger even when live APIs fail because we fall back to a deterministic
            provider — so trades never break.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="providers-refresh"
            onClick={load}
            className="border border-neo-border px-4 py-3 font-mono uppercase tracking-widest text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
          >
            <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
            Refresh
          </button>
          <button
            data-testid="providers-save"
            onClick={save}
            disabled={!dirty || busy}
            className="bg-white text-black border border-white px-5 py-3 font-mono uppercase tracking-widest text-xs hover-invert disabled:opacity-40 flex items-center gap-2"
          >
            <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
            {busy ? "Saving…" : `Save${dirty ? ` (${Object.keys(drafts).length})` : ""}`}
          </button>
        </div>
      </header>

      <div className="space-y-6">
        {GROUPS.map((g) => {
          const groupStatus = status[g.id];
          const rows = providers.filter((p) => p.group === g.id);
          const isPayments = g.id === "payments";
          const isEmail = g.id === "email";
          const skipAutoPanel = isPayments || isEmail;
          return (
            <section
              key={g.id}
              data-testid={`providers-group-${g.id}`}
              className="border border-neo-border bg-neo-surface"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-neo-border">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    {g.label}
                  </div>
                  <div className="mt-1 text-xl font-bold tracking-tight">{g.provider}</div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {groupStatus?.current && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 border border-white/30 bg-white/5 text-white text-[10px] uppercase tracking-widest font-mono"
                      data-testid={`providers-current-${g.id}`}
                    >
                      <Trophy className="w-3 h-3" />
                      Current: {groupStatus.current}
                    </span>
                  )}
                  <button
                    data-testid={`providers-test-${g.id}`}
                    onClick={() => runTest(g.id)}
                    disabled={!!testing[g.id]}
                    className="border border-neo-border px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:bg-white/5 transition-colors flex items-center gap-2 disabled:opacity-60"
                  >
                    {testing[g.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    {isEmail ? "Send test email" : "Test all providers"}
                  </button>
                </div>
              </div>

              {/* Auto-optimizer panel (skip for payments & email) */}
              {!skipAutoPanel && groupStatus && (
                <div className="p-5 border-b border-neo-border">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                      <Zap className="w-3 h-3" strokeWidth={2} />
                      auto-optimizer
                    </div>
                    <ModeSelector
                      group={g.id}
                      current={groupStatus.mode}
                      options={["auto", ...groupStatus.providers.map((p) => p.id)]}
                      onChange={(m) => setMode(g.id, m)}
                    />
                  </div>
                  <div className="border border-neo-border" data-testid={`providers-metrics-${g.id}`}>
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-neo-border font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                      <div className="col-span-3">Provider</div>
                      <div className="col-span-2">Kind</div>
                      <div className="col-span-1 text-right">OK</div>
                      <div className="col-span-1 text-right">Fail</div>
                      <div className="col-span-2 text-right">Success</div>
                      <div className="col-span-2 text-right">Avg latency</div>
                      <div className="col-span-1 text-right">Score</div>
                    </div>
                    {groupStatus.providers.map((p, idx) => (
                      <div
                        key={p.id}
                        className={`grid grid-cols-12 gap-2 px-3 py-2 font-mono text-xs items-center ${
                          p.id === groupStatus.current ? "bg-white/5" : ""
                        }`}
                        data-testid={`providers-metric-row-${g.id}-${p.id}`}
                      >
                        <div className="col-span-3">
                          <span className="text-white">{p.label}</span>{" "}
                          {idx === 0 && groupStatus.mode === "auto" && (
                            <span className="text-[9px] text-[#00FF66] uppercase tracking-widest">leader</span>
                          )}
                        </div>
                        <div className="col-span-2 uppercase text-neutral-400 text-[10px] tracking-widest">
                          {p.kind}
                        </div>
                        <div className="col-span-1 text-right pos">{p.success}</div>
                        <div className="col-span-1 text-right neg">{p.fail}</div>
                        <div className="col-span-2 text-right">
                          {p.success_rate != null
                            ? `${(p.success_rate * 100).toFixed(1)}%`
                            : "—"}
                        </div>
                        <div className="col-span-2 text-right">
                          {p.avg_latency_ms != null ? `${Math.round(p.avg_latency_ms)} ms` : "—"}
                        </div>
                        <div className="col-span-1 text-right text-white">{p.score}</div>
                      </div>
                    ))}
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-neo-border font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                      <div className="col-span-12">
                        Last used ·{" "}
                        <span className="text-white">
                          {groupStatus.last_used || "not-yet-called"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Credentials / endpoints */}
              <div className="divide-y divide-neo-border">
                {rows.map((row) => (
                  <ProviderRow
                    key={row.key}
                    row={row}
                    draft={drafts[row.key]}
                    reveal={reveal[row.key]}
                    onToggleReveal={() =>
                      setReveal((r) => ({ ...r, [row.key]: !r[row.key] }))
                    }
                    onChange={(v) => setDraft(row.key, v)}
                    onClearDraft={() => clearDraft(row.key)}
                    onReset={() => reset(row.key)}
                    busy={busy}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function ModeSelector({ group, current, options, onChange }) {
  return (
    <div
      className="flex border border-neo-border font-mono uppercase tracking-widest text-[10px]"
      data-testid={`providers-mode-${group}`}
    >
      {options.map((opt) => (
        <button
          key={opt}
          data-testid={`providers-mode-${group}-${opt}`}
          onClick={() => onChange(opt)}
          className={`px-3 py-2 border-r border-neo-border last:border-r-0 transition-colors ${
            current === opt ? "bg-white text-black" : "text-neutral-400 hover:bg-white/5"
          }`}
        >
          {opt === "auto" ? "★ auto" : opt}
        </button>
      ))}
    </div>
  );
}

function ProviderRow({ row, draft, reveal, onToggleReveal, onChange, onClearDraft, onReset, busy }) {
  const hasDraft = draft !== undefined;
  return (
    <div
      className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
      data-testid={`provider-row-${row.key}`}
    >
      <div className="md:col-span-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          {row.provider}
        </div>
        <div className="mt-1 text-sm font-bold">{row.label}</div>
        <div className="mt-1 text-xs text-neutral-500">{row.help}</div>
        <div className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest">
          {row.using_default ? (
            <span className="text-neutral-500">using env default</span>
          ) : (
            <span className="pos">custom override</span>
          )}
        </div>
      </div>
      <div className="md:col-span-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          Current value
        </div>
        <div className="mt-2 font-mono text-sm text-neutral-300 break-all">{row.value}</div>
        <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 flex items-center justify-between">
          <span>New value</span>
          {row.secret && (
            <button
              type="button"
              onClick={onToggleReveal}
              className="inline-flex items-center gap-1 text-neutral-500 hover:text-white transition-colors"
              data-testid={`provider-reveal-${row.key}`}
            >
              {reveal ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {reveal ? "hide" : "show"}
            </button>
          )}
        </div>
        <input
          data-testid={`provider-input-${row.key}`}
          type={row.secret && !reveal ? "password" : "text"}
          value={draft ?? ""}
          placeholder={row.secret ? "Paste new secret to override" : "Paste new URL to override"}
          onChange={(e) => onChange(e.target.value)}
          className="mt-2 w-full bg-transparent border border-neo-border px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-white transition-colors"
        />
        {hasDraft && (
          <button
            data-testid={`provider-clear-draft-${row.key}`}
            onClick={onClearDraft}
            className="mt-2 text-[10px] font-mono uppercase tracking-widest text-neutral-500 hover:text-white transition-colors"
          >
            × discard draft
          </button>
        )}
      </div>
      <div className="md:col-span-2 flex md:justify-end">
        <button
          data-testid={`provider-reset-${row.key}`}
          onClick={onReset}
          disabled={busy || row.using_default}
          className="border border-neo-border px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:bg-white/5 transition-colors disabled:opacity-40"
        >
          Reset to default
        </button>
      </div>
    </div>
  );
}
